import React, { useState, useEffect } from 'react';

const API = 'https://api.botanix.in/api/razorpay';
const RAZORPAY_KEY = 'rzp_live_SxpWLqtVL7xmjU';

const C = {
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  gold:'#C9A84C', accent:'#3AAF58', accentBg:'rgba(58,175,88,.12)',
  red:'#EF4444', redBg:'rgba(239,68,68,.12)', amber:'#F59E0B',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const loadRazorpayScript = () => new Promise(resolve => {
  if (document.getElementById('razorpay-script')) return resolve(true);
  const script = document.createElement('script');
  script.id = 'razorpay-script';
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

export default function PaymentModal({ booking, guest, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('advance');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    loadRazorpayScript();
    // Load payment status
    if (booking?.booking_code) {
      fetch(`${API}/status/${booking.booking_code}`)
        .then(r=>r.json())
        .then(d=>setPaymentStatus(d))
        .catch(()=>{});
    }
  }, [booking]);

  const balance = parseFloat(booking?.balance_amount||0);
  const totalAmount = parseFloat(booking?.total_amount||0);
  const advancePaid = parseFloat(booking?.advance_paid||0);

  const handlePayment = async () => {
    const payAmount = parseFloat(amount);
    if (!payAmount || payAmount <= 0) { setMsg('Enter a valid amount'); return; }
    if (payAmount > balance) { setMsg(`Amount cannot exceed balance due: ${inr(balance)}`); return; }

    setLoading(true);
    setMsg('');

    try {
      // Load Razorpay
      const loaded = await loadRazorpayScript();
      if (!loaded) { setMsg('Failed to load payment gateway'); setLoading(false); return; }

      // Create order
      const orderRes = await fetch(`${API}/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: payAmount,
          bookingId: booking.booking_code,
          guestName: guest?.full_name || 'Guest',
          guestEmail: guest?.email || '',
          guestPhone: guest?.phone || '',
          paymentType,
          description: `${paymentType === 'advance' ? 'Advance' : 'Balance'} payment for ${booking.booking_code}`,
        })
      });

      const order = await orderRes.json();
      if (!orderRes.ok) { setMsg('Order creation failed: ' + order.error); setLoading(false); return; }

      // Open Razorpay checkout
      const options = {
        key: RAZORPAY_KEY,
        amount: order.amount,
        currency: 'INR',
        name: 'Botanix Nature Resort',
        description: `Booking ${booking.booking_code}`,
        image: 'https://botanix.in/wp-content/uploads/2023/01/botanix-logo.png',
        order_id: order.orderId,
        prefill: {
          name: guest?.full_name || '',
          email: guest?.email || '',
          contact: guest?.phone || '',
        },
        theme: { color: '#C9A84C' },
        handler: async (response) => {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API}/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                bookingId: booking.booking_code,
                amount: payAmount,
                paymentType,
              })
            });
            const verified = await verifyRes.json();
            if (verified.success) {
              setStatus('success');
              setMsg(`✅ Payment of ${inr(payAmount)} received successfully!`);
              if (onSuccess) onSuccess(verified);
            } else {
              setStatus('failed');
              setMsg('Payment verification failed. Please contact support.');
            }
          } catch(e) {
            setStatus('failed');
            setMsg('Verification error: ' + e.message);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setMsg('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setLoading(false);
    } catch(e) {
      setMsg('Error: ' + e.message);
      setLoading(false);
    }
  };

  const sendPaymentLink = async () => {
    if (!guest?.phone) { setMsg('No phone number for guest'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: guest.phone,
          bookingId: booking.booking_code,
          amount: balance,
          guestName: guest.full_name,
        })
      });
      const d = await r.json();
      if (d.success) setMsg('✅ Payment link sent to guest!');
      else setMsg('Failed to send link');
    } catch(e) { setMsg('Error: ' + e.message); }
    setLoading(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:16,padding:28,width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto'}}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.text}}>💳 Online Payment</div>
            <div style={{fontSize:12,color:C.text3,marginTop:2}}>{booking?.booking_code}</div>
          </div>
          <button onClick={onClose} style={{background:'transparent',border:'none',color:C.text3,fontSize:20,cursor:'pointer'}}>✕</button>
        </div>

        {/* Booking Summary */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:16}}>
          <div style={{fontSize:12,color:C.text3,marginBottom:10,textTransform:'uppercase'}}>Booking Summary</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            {[
              {label:'Total Amount',value:inr(totalAmount),color:C.text},
              {label:'Advance Paid',value:inr(advancePaid),color:C.accent},
              {label:'Balance Due',value:inr(balance),color:balance>0?C.red:C.accent},
            ].map((s,i)=>(
              <div key={i} style={{textAlign:'center',padding:'10px',background:C.surface2,borderRadius:8}}>
                <div style={{fontSize:15,fontWeight:800,color:s.color}}>{s.value}</div>
                <div style={{fontSize:10,color:C.text3,marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>
          {paymentStatus?.paymentCount>0&&(
            <div style={{marginTop:10,fontSize:12,color:C.accent}}>
              ✅ {paymentStatus.paymentCount} online payment(s) received: {inr(paymentStatus.totalPaid)}
            </div>
          )}
        </div>

        {status==='success' ? (
          <div style={{textAlign:'center',padding:30}}>
            <div style={{fontSize:48,marginBottom:12}}>✅</div>
            <div style={{fontSize:18,fontWeight:800,color:C.accent,marginBottom:8}}>Payment Successful!</div>
            <div style={{fontSize:13,color:C.text2,marginBottom:20}}>{msg}</div>
            <button onClick={onClose}
              style={{padding:'10px 24px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
              Done
            </button>
          </div>
        ) : balance <= 0 ? (
          <div style={{textAlign:'center',padding:30,background:C.accentBg,borderRadius:12,border:`1px solid ${C.accent}44`}}>
            <div style={{fontSize:32,marginBottom:10}}>✅</div>
            <div style={{fontSize:16,fontWeight:700,color:C.accent}}>Fully Paid!</div>
            <div style={{fontSize:13,color:C.text2,marginTop:4}}>No balance due for this booking</div>
          </div>
        ) : (
          <div>
            {/* Payment Type */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:C.text3,marginBottom:8}}>Payment Type</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                {[['advance','Advance Payment'],['balance','Balance Payment']].map(([val,label])=>(
                  <button key={val} onClick={()=>{setPaymentType(val);setAmount(val==='balance'?balance.toString():'');}}
                    style={{padding:'10px',background:paymentType===val?`${C.gold}22`:C.surface,border:`1px solid ${paymentType===val?C.gold:C.border}`,borderRadius:8,color:paymentType===val?C.gold:C.text2,fontSize:13,fontWeight:paymentType===val?700:400,cursor:'pointer'}}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,color:C.text3,marginBottom:6}}>Amount to Pay</div>
              <div style={{display:'flex',gap:8}}>
                <input type="number" value={amount} onChange={e=>setAmount(e.target.value)}
                  placeholder={`Max: ${inr(balance)}`}
                  style={{flex:1,padding:'10px 14px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:15,outline:'none'}} />
                <button onClick={()=>setAmount(balance.toString())}
                  style={{padding:'10px 14px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>
                  Full Balance
                </button>
              </div>
            </div>

            {msg && (
              <div style={{padding:'10px 14px',borderRadius:8,marginBottom:14,fontSize:13,
                background:msg.includes('✅')?C.accentBg:C.redBg,
                color:msg.includes('✅')?C.accent:C.red}}>
                {msg}
              </div>
            )}

            {/* Pay Button */}
            <button onClick={handlePayment} disabled={loading||!amount}
              style={{width:'100%',padding:'14px',background:loading||!amount?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,
                color:loading||!amount?C.text3:'#1A1A1A',border:'none',borderRadius:10,fontSize:15,fontWeight:800,
                cursor:loading||!amount?'default':'pointer',marginBottom:10}}>
              {loading ? '⏳ Processing...' : ('💳 Pay ' + (amount ? inr(parseFloat(amount)) : '') + ' Online')}
            </button>

            {/* Send Payment Link */}
            <button onClick={sendPaymentLink} disabled={loading}
              style={{width:'100%',padding:'12px',background:'rgba(37,211,102,.1)',color:'#25D366',
                border:'1px solid rgba(37,211,102,.3)',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer'}}>
              💬 Send Payment Link via WhatsApp
            </button>

            <div style={{marginTop:12,textAlign:'center',fontSize:11,color:C.text3}}>
              🔒 Secured by Razorpay · UPI · Cards · Net Banking · Wallets
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
